import { CarouselItemId } from '@homex/se-widget-flow-types';
import { useSelector } from 'react-redux';
import { useTheme } from 'styled-components/macro';

import {
  setCurrentCarouselItem,
  setCurrentCarouselItemToPrevious,
} from 'actions';
import { CarouselBottomButton } from 'components/CarouselBottomButton/CarouselBottomButton';
import { ReactComponent as CloseSvg } from 'components/svg/Close.svg';
import { useThunkDispatch } from 'hooks/useThunkDispatch';
import { CarouselReduxReducer, IState, MediaDetailCarouselItem } from 'typings';
import { tryGetFlowCarouselItem } from 'utils/tryGetFlowCarouselItem';

import {
  Buttons,
  CloseButton,
  Container,
  FormGroup,
  HeaderLower,
  Media,
  MediaContainer,
} from './styles';

export const CAROUSEL_ITEM_ID = CarouselItemId.MediaDetail;

export const CarouselItemMediaUploadDetail = () => {
  const dispatch = useThunkDispatch();
  const { flow, selectedFile } = useSelector((state: IState) => ({
    flow: state.client.flow,
    selectedFile: state.userInput.mediaUploadSelection,
  }));

  const flowCarouselItem = tryGetFlowCarouselItem<MediaDetailCarouselItem>(
    flow,
    CAROUSEL_ITEM_ID,
  );

  const { buttonType, repairCommentsEnabled } = useTheme();

  const bottomButtonFormat = flow?.buttonType ?? buttonType;

  const getMedia = () => {
    if (!selectedFile) {
      return;
    }

    if (/video/.test(selectedFile.type)) {
      return (
        <Media controls={true} key={selectedFile.preview} preload="auto">
          {['video/mp4', 'video/webm', 'video/ogg'].map((type) => (
            <source key={type} src={selectedFile.preview} type={type} />
          ))}
          Video link invalid
        </Media>
      );
    } else {
      return <Media as="img" src={selectedFile.preview} />;
    }
  };

  const handleCloseClick = () => {
    let destinationItemId;

    if (flowCarouselItem) {
      destinationItemId = flowCarouselItem.options.destinations.close;
    } else {
      destinationItemId = CarouselItemId.MediaUpload;
    }

    dispatch(
      setCurrentCarouselItemToPrevious(
        CarouselReduxReducer.SERVICE,
        destinationItemId,
      ),
    );
  };

  const handleContinueClick = () => {
    let nextCarouselItemId;

    if (flowCarouselItem) {
      nextCarouselItemId = flowCarouselItem.options.destinations.next;
    } else {
      nextCarouselItemId = repairCommentsEnabled
        ? CarouselItemId.RepairComments
        : CarouselItemId.CustomerLookup;
    }

    dispatch(
      setCurrentCarouselItem(CarouselReduxReducer.SERVICE, nextCarouselItemId),
    );
  };

  // Get everything after the last slash, then everything before the first
  // question mark
  const fileName =
    (selectedFile &&
      selectedFile.name.split('/').reverse()[0]?.split('?')[0]) ??
    '';

  return (
    <FormGroup>
      <Container>
        <HeaderLower>{fileName}</HeaderLower>
        <MediaContainer>
          {getMedia()}
          <CloseButton icon={<CloseSvg />} onClick={handleCloseClick} />
        </MediaContainer>
      </Container>
      <Buttons>
        <CarouselBottomButton
          buttonFormat={bottomButtonFormat}
          carouselItemId={CAROUSEL_ITEM_ID}
          continueButtonLabel="Continue"
          onContinueClick={handleContinueClick}
        />
      </Buttons>
    </FormGroup>
  );
};
